import { ChatRoomMessage } from '@/models/chat.model';
import {useAppSelector} from '@/lib/store';
import {useState} from "react";
import ImageModal from "@/app/user/messages/components/image-modal.tsx";
import RichMessage from "./rich-message";
import ApprovalMessage from "./approval-message";
import AssignmentTriggerMessage from "./assignment-trigger-message";

interface Props { msg: ChatRoomMessage; }

export default function MessageBubble({ msg }: Props) {
    const { user: me } = useAppSelector((state) => state.auth);
    const users = useAppSelector(s => s.users);
    const name  = users[msg.sender]?.name || msg.sender.slice(0,6);
    const isSent = msg.sender === me?.id;
    const [open, setOpen] = useState(false);

    const meta = msg.meta_data as any;

    if (msg.is_file && meta?.file) {
        const { url, name: fileName, mime } = meta.file;
        const isImg = mime.startsWith("image");

        return (
            <>
                <div className={`flex flex-col gap-1 max-w-[70%] ${isSent ? 'items-end ml-auto' : 'items-start mr-auto'}`}>
                    <p className="text-xs font-medium text-muted-foreground px-3">{isSent ? "You" : name}</p>
                    <div className={`inline-block rounded-2xl overflow-hidden ${
                        isSent 
                            ? 'bg-primary text-primary-foreground rounded-br-md' 
                            : 'bg-muted text-foreground rounded-bl-md'
                    }`}>
                        {isImg ? (
                            <img
                                alt={fileName}
                                src={url}
                                className="h-32 max-w-[200px] object-cover cursor-pointer"
                                onClick={() => setOpen(true)}
                            />
                        ) : (
                            <a href={url} download target="_blank" className="px-3 py-2 block text-sm hover:underline">
                                📎 {fileName}
                            </a>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground px-3">{new Date(msg.created_at).toLocaleTimeString()}</p>

                    {isImg && open && (
                        <ImageModal
                            src={url}
                            alt={fileName}
                            onClose={() => setOpen(false)}
                            downloadName={fileName}
                        />
                    )}
                </div>
            </>
        );
    }

    // System messages are styled differently - centered, subtle, no bubble
    if (msg.is_system) {
        return (
            <div className="flex flex-col items-center space-y-2 my-3">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <span className="h-px bg-border flex-1 max-w-8"></span>
                    <span className="px-2 py-1 text-center">{msg.message}</span>
                    <span className="h-px bg-border flex-1 max-w-8"></span>
                </div>
                {msg.meta_data && (
                    <>
                        {(msg.meta_data as any)?.type === "approval_request" ? (
                            <ApprovalMessage 
                                messageId={msg.id}
                                approvalData={(msg.meta_data as any).approvalData}
                                isProcessed={(msg.meta_data as any)?.processed || false}
                            />
                        ) : (msg.meta_data as any)?.type === "trigger_assignment_modal" ? (
                            <AssignmentTriggerMessage 
                                messageId={msg.id}
                                appointmentId={(msg.meta_data as any).appointmentId}
                                triggeredByName={(msg.meta_data as any).triggeredByName}
                                isProcessed={(msg.meta_data as any)?.processed || false}
                            />
                        ) : (
                            <RichMessage metaData={msg.meta_data} />
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div className={`flex flex-col gap-1 max-w-[70%] ${isSent ? 'items-end ml-auto' : 'items-start mr-auto'}`}>
            <p className="text-xs font-medium text-muted-foreground px-3">{isSent ? "You" : name}</p>
            <div className={`inline-block rounded-2xl px-3 py-2 ${
                isSent 
                    ? 'bg-primary text-primary-foreground rounded-br-md' 
                    : 'bg-muted text-foreground rounded-bl-md'
            }`}>
                <p className="text-sm whitespace-pre-line break-words">{msg.message}</p>
            </div>
            <p className="text-xs text-muted-foreground px-3">{new Date(msg.created_at).toLocaleTimeString()}</p>
        </div>
    );
}